import React, { useState } from 'react';
import { contactData, logoUrl } from '../data/mock';
import { Phone, Mail, MapPin, Send, Instagram, Facebook, Clock, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    eventDate: '',
    guestCount: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = (e) => {
  e.preventDefault();

  if (!formData.name || !formData.phone || !formData.eventDate || !formData.guestCount) {
    toast.error("Please fill all required fields");
    return;
  }

  const message = `New Inquiry - Elite Marriage Lawns

Name: ${formData.name}
Phone: ${formData.phone}
Event Date: ${formData.eventDate}
Guest Count: ${formData.guestCount}
Message: ${formData.message || "Not provided"}`;

  const encodedMessage = encodeURIComponent(message);

  const phoneNumber = "917607837676"; // 🔴 REPLACE WITH YOUR NUMBER

  window.open(
    `https://wa.me/${phoneNumber}?text=${encodedMessage}`,
    "_blank"
  );

  toast.success("Redirecting to WhatsApp...");
};


  return (
    <section id="contact" className="py-20 md:py-28 bg-[#F5F3F0]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="font-sans text-sm text-[#C9A962] tracking-[0.2em] uppercase mb-4 block">
            Get In Touch
          </span>
          <h2 className="section-title text-3xl md:text-4xl">Book Your Date</h2>
          <div className="decorative-line" />
          <p className="section-subtitle max-w-2xl mx-auto">
            Ready to create unforgettable memories? Reach out to us today!
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Contact Info */}
          <div>
            <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm mb-8">
              <h3 className="font-serif text-2xl text-[#2C2C2C] mb-6">Contact Information</h3>

              <div className="space-y-6">
                {/* Phone */}
                <a
                  href={`tel:${contactData.phone}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A962] transition-colors">
                    <Phone size={20} className="text-[#C9A962] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6259] uppercase tracking-wider mb-1">Phone</p>
                    <p className="text-[#2C2C2C] font-medium text-lg group-hover:text-[#C9A962] transition-colors">
                      {contactData.phone}
                    </p>
                  </div>
                </a>

                {/* Email */}
                <a
                  href={`mailto:${contactData.email}`}
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A962] transition-colors">
                    <Mail size={20} className="text-[#C9A962] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6259] uppercase tracking-wider mb-1">Email</p>
                    <p className="text-[#2C2C2C] font-medium group-hover:text-[#C9A962] transition-colors">
                      {contactData.email}
                    </p>
                  </div>
                </a>

                {/* Address */}
                <a
                  href={contactData.mapUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-start gap-4 group"
                >
                  <div className="w-12 h-12 rounded-full bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#C9A962] transition-colors">
                    <MapPin size={20} className="text-[#C9A962] group-hover:text-white transition-colors" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6259] uppercase tracking-wider mb-1">Address</p>
                    <p className="text-[#2C2C2C] font-medium leading-relaxed group-hover:text-[#C9A962] transition-colors">
                      {contactData.address}
                    </p>
                  </div>
                </a>

                {/* Hours */}
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-full bg-[#C9A962]/10 flex items-center justify-center flex-shrink-0">
                    <Clock size={20} className="text-[#C9A962]" />
                  </div>
                  <div>
                    <p className="text-sm text-[#6B6259] uppercase tracking-wider mb-1">Available</p>
                    <p className="text-[#2C2C2C] font-medium">Mon - Sun: 9:00 AM - 9:00 PM</p>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="mt-8 pt-8 border-t border-[#D4C4B0]/30">
                <p className="text-sm text-[#6B6259] uppercase tracking-wider mb-4">Follow Us</p>
                <div className="flex gap-3">
                  <a
                    href={contactData.socialLinks.instagram}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#C9A962]/10 flex items-center justify-center hover:bg-[#C9A962] group transition-colors"
                    aria-label="Instagram"
                  >
                    <Instagram size={18} className="text-[#C9A962] group-hover:text-white transition-colors" />
                  </a>
                  <a
                    href={contactData.socialLinks.facebook}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-full bg-[#C9A962]/10 flex items-center justify-center hover:bg-[#C9A962] group transition-colors"
                    aria-label="Facebook"
                  >
                    <Facebook size={18} className="text-[#C9A962] group-hover:text-white transition-colors" />
                  </a>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="rounded-2xl overflow-hidden shadow-sm h-64">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3558.4!2d81.9!3d26.85!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjbCsDUxJzAwLjAiTiA4McKwNTQnMDAuMCJF!5e0!3m2!1sen!2sin!4v1234567890!5m2!1sen!2sin"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Elite Marriage Lawns Location"
              />
            </div>
          </div>

          {/* Inquiry Form */}
          <div className="bg-white rounded-2xl p-8 md:p-10 shadow-sm h-fit">
            <h3 className="font-serif text-2xl text-[#2C2C2C] mb-2">Inquiry Form</h3>
            <p className="text-[#6B6259] mb-8">
              Fill in your details and we'll get back to you within 24 hours.
            </p>

            {isSubmitted ? (
              <div className="text-center py-12">
                <div className="w-20 h-20 rounded-full bg-[#8B9A7D]/20 flex items-center justify-center mx-auto mb-6">
                  <CheckCircle size={40} className="text-[#8B9A7D]" />
                </div>
                <h4 className="font-serif text-2xl text-[#2C2C2C] mb-2">Thank You!</h4>
                <p className="text-[#6B6259]">
                  We've received your inquiry. Our team will contact you soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                {/* Name */}
                <div>
                  <label
                    htmlFor="name"
                    className="block text-sm text-[#6B6259] uppercase tracking-wider mb-2"
                  >
                    Your Name *
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 bg-[#F5F3F0] border border-transparent rounded-lg focus:border-[#C9A962] focus:outline-none transition-colors"
                    placeholder="Enter your full name"
                  />
                </div>

                {/* Email & Phone */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm text-[#6B6259] uppercase tracking-wider mb-2"
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-4 py-3 bg-[#F5F3F0] border border-transparent rounded-lg focus:border-[#C9A962] focus:outline-none transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm text-[#6B6259] uppercase tracking-wider mb-2"
                    >
                      Phone *
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-[#F5F3F0] border border-transparent rounded-lg focus:border-[#C9A962] focus:outline-none transition-colors"
                      placeholder="Your phone number"
                    />
                  </div>
                </div>

                {/* Event Date & Guest Count */}
                <div className="grid md:grid-cols-2 gap-5">
                  <div>
                    <label
                      htmlFor="eventDate"
                      className="block text-sm text-[#6B6259] uppercase tracking-wider mb-2"
                    >
                      Event Date *
                    </label>
                    <input
                      type="date"
                      id="eventDate"
                      name="eventDate"
                      value={formData.eventDate}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-[#F5F3F0] border border-transparent rounded-lg focus:border-[#C9A962] focus:outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="guestCount"
                      className="block text-sm text-[#6B6259] uppercase tracking-wider mb-2"
                    >
                      Guest Count *
                    </label>
                    <select
                      id="guestCount"
                      name="guestCount"
                      value={formData.guestCount}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 bg-[#F5F3F0] border border-transparent rounded-lg focus:border-[#C9A962] focus:outline-none transition-colors"
                    >
                      <option value="">Select guest count</option>
                      <option value="50-100">50 - 100 guests</option>
                      <option value="100-200">100 - 200 guests</option>
                      <option value="200-500">200 - 500 guests</option>
                      <option value="500-1000">500 - 1000 guests</option>
                      <option value="1000+">1000+ guests</option>
                    </select>
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label
                    htmlFor="message"
                    className="block text-sm text-[#6B6259] uppercase tracking-wider mb-2"
                  >
                    Your Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows={4}
                    className="w-full px-4 py-3 bg-[#F5F3F0] border border-transparent rounded-lg focus:border-[#C9A962] focus:outline-none transition-colors resize-none"
                    placeholder="Tell us about your event..."
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? (
                    <>
                      <span className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      Send Inquiry
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
